/**
 * Availability Service
 *
 * GET /api/public/availability?date=YYYY-MM-DD&service=<slug>
 * (contract v1 §3.3). `date` is a Nassau calendar day; slots come back as
 * UTC instants and are passed through unchanged as startISO/endISO, so the
 * booking request can send slot.startISO straight back as start_time.
 */

import { get } from './apiClient';
import { getServiceSlug } from '../utils/serviceMapper';
import { formatDate, isDateKey } from '../utils/dateUtils';

/**
 * Map one API slot to the frontend slot shape.
 * @param {{start: string, end: string}} s
 * @returns {{startISO: string, endISO: string}}
 */
export const normalizeSlot = (s) => ({ startISO: s.start, endISO: s.end });

/**
 * Normalize the availability `data` object.
 * @param {object} data - Envelope data
 * @returns {{date: string, timeZone: string, service: string, slots: Array<{startISO: string, endISO: string}>}}
 */
export const normalizeAvailability = (data) => ({
  date: data.date,
  timeZone: data.time_zone,
  service: data.service,
  slots: (Array.isArray(data.slots) ? data.slots : [])
    .filter((s) => s && typeof s.start === 'string' && typeof s.end === 'string')
    .map(normalizeSlot),
});

/**
 * Bookable start times for one service on one day.
 *
 * @param {string|Date} date - 'YYYY-MM-DD' (Nassau calendar day) or a picker Date
 * @param {string|{slug: string}} service - Service slug (or service object)
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Array<{startISO: string, endISO: string}>>} Sorted slots; may be empty
 * @throws {ApiError|Error} ApiError from the API (message already user-facing)
 */
export const getAvailability = async (date, service, { signal } = {}) => {
  if (!date) {
    throw new Error('Date is required');
  }
  const dateKey = formatDate(date);
  if (!isDateKey(dateKey)) {
    throw new Error('Date must be a valid YYYY-MM-DD day');
  }
  const slug = getServiceSlug(service);

  const data = await get('/api/public/availability', {
    params: { date: dateKey, service: slug },
    signal,
  });

  return normalizeAvailability(data).slots;
};
